import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import { Icon, Typography, Button } from '@fdha/web-ui-library';
import { Maybe, MealOptionType } from '@fdha/graphql-api-patient';
import { SectionType } from '@fdha/common-utils';
import { MenuOptionSelection as MealItem } from '@fdha/graphql-api-patient';

import { getStyles } from './MealSection.styles';
import MealItemComponent from './MealItem';

interface MealSectionProps {
  title: string;
  sectionType: SectionType;
  items?: MealItem[];
  preparedItems?: MealItem[];
  mealKitItems?: MealItem[];
  selectedCount: number;
  requiredCount: number;
  isComplete: boolean;
  isReadOnly: boolean;
  hasMultipleVariants?: (menuOptionId: string) => boolean;
  allowSelfPreparedMeals?: boolean;
  onQuantityChange: (
    menuOptionId: string,
    quantity: number,
    mealkitVariantId?: string | Maybe<string>
  ) => void;
  onItemClick: (item: MealItem) => void;
  showAddButton?: boolean;
  addButtonText?: string;
  showMealTypeSeparation?: boolean;
  onAddItems?: (itemType: SectionType) => void;
}

const MealSection: React.FC<MealSectionProps> = ({
  title,
  sectionType,
  items,
  preparedItems: propPreparedItems,
  mealKitItems: propMealKitItems,
  selectedCount,
  requiredCount,
  isReadOnly,
  hasMultipleVariants,
  onQuantityChange,
  onItemClick,
  showAddButton = false,
  addButtonText = 'Add items',
  showMealTypeSeparation = false,
  onAddItems,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);

  const handleQuantityChange = (item: MealItem, newQuantity: number) => {
    onQuantityChange(item.menuOptionId, newQuantity, item.mealkitVariantId);
  };

  const handleItemClick = (item: MealItem) => {
    onItemClick(item);
  };

  // Use provided separated items or compute from items array
  const { preparedItems, mealKitItems, allItems } = useMemo(() => {
    // If pre-separated items are provided, use them directly
    if (propPreparedItems || propMealKitItems) {
      return {
        preparedItems: propPreparedItems || [],
        mealKitItems: propMealKitItems || [],
        allItems: [...(propPreparedItems || []), ...(propMealKitItems || [])],
      };
    }

    // Fallback to items array
    const itemsArray = items || [];
    if (!showMealTypeSeparation) {
      return {
        preparedItems: [],
        mealKitItems: [],
        allItems: itemsArray,
      };
    }

    return {
      preparedItems: itemsArray.filter(
        (item) => item.mealOptionType === MealOptionType.Dish
      ),
      mealKitItems: itemsArray.filter(
        (item) => item.mealOptionType === MealOptionType.MealKit
      ),
      allItems: itemsArray,
    };
  }, [items, propPreparedItems, propMealKitItems, showMealTypeSeparation]);

  // Helper function to render a list of meal items
  const renderMealItems = (sectionItems: MealItem[]) =>
    sectionItems.map((item) => (
      <MealItemComponent
        key={
          item.mealkitVariantId
            ? `${item.menuOptionId}-${item.mealkitVariantId}`
            : item.menuOptionId
        }
        item={item}
        isReadOnly={isReadOnly}
        hasMultipleVariants={hasMultipleVariants}
        hasReachedRequired={selectedCount >= requiredCount}
        onQuantityChange={handleQuantityChange}
        onItemClick={handleItemClick}
      />
    ));

  // Helper function to render a subsection with title
  const renderSubsection = (title: string, sectionItems: MealItem[]) => {
    if (sectionItems.length === 0) return null;

    // Determine the i18n key based on the title
    const getSubheaderKey = () => {
      if (title === 'Prepared meal')
        return 'meals:reviewDelivery.subheader.dish';
      if (title === 'Meal kit') return 'meals:reviewDelivery.subheader.mealKit';
      return 'common:value';
    };

    const i18nKey = getSubheaderKey();
    const i18nParams =
      i18nKey === 'common:value' ? { value: title } : undefined;

    return (
      <>
        <Typography
          variant="subtitle1"
          sx={styles.subsectionTitle}
          i18nKey={i18nKey}
          i18nParams={i18nParams}
        >
          {title}
        </Typography>
        {renderMealItems(sectionItems)}
      </>
    );
  };

  // Determine validation state and icon
  const getValidationIcon = () => {
    if (selectedCount === requiredCount) {
      // Correct quantity - green checkmark
      return (
        <Icon
          name="checkmark-circle-2-outline"
          size="medium"
          fill={theme.palette.success.main}
        />
      );
    }
    if (selectedCount > requiredCount) {
      // Excess quantity - orange triangle warning
      return (
        <Icon
          name="alert-triangle-outline"
          size="medium"
          fill={theme.palette.warning.main}
        />
      );
    }
    // Insufficient quantity - red exclamation
    return (
      <Icon
        name="alert-circle-outline"
        size="medium"
        fill={theme.palette.error.main}
      />
    );
  };

  const descriptionText =
    sectionType === 'entreeDishes' ? 'entree dishes' : 'snacks/sides';

  const showSubSections =
    showMealTypeSeparation || propPreparedItems || propMealKitItems;

  return (
    <>
      <Box sx={styles.container}>
        {/* Section Header */}
        <Box sx={styles.sectionHeader}>
          <Typography
            variant="h5"
            sx={styles.title}
            i18nKey={
              sectionType === 'entreeDishes'
                ? 'meals:section.entrees'
                : 'meals:section.snacks'
            }
          >
            {title}
          </Typography>

          <Box sx={styles.validationContainer}>
            {getValidationIcon()}
            <Typography
              variant="body2"
              i18nKey={
                sectionType === 'entreeDishes'
                  ? 'meals:reviewDelivery.selectedMessage.entrees'
                  : 'meals:reviewDelivery.selectedMessage.snacks'
              }
              i18nComponents={{
                strongQuantity: (
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ fontWeight: 600 }}
                    i18nKey="meals:reviewDelivery.selectedOfTotal"
                    i18nParams={{
                      selected: selectedCount,
                      total: requiredCount,
                    }}
                  >
                    {selectedCount} out of {requiredCount}
                  </Typography>
                ),
              }}
            >
              {`You have selected ${selectedCount} out of ${requiredCount} ${descriptionText}`}
            </Typography>
          </Box>
        </Box>

        {/* Add Button */}
        {showAddButton && (
          <Button
            variant="text"
            sx={styles.addButton}
            startEvaIcon={{ name: 'plus-outline' }}
            onClick={() => {
              onAddItems?.(sectionType);
            }}
            i18nKey={
              sectionType === 'entreeDishes'
                ? 'meals:reviewDelivery.addButton.entrees'
                : 'meals:reviewDelivery.addButton.snacks'
            }
          >
            {addButtonText}
          </Button>
        )}

        {/* Meal Items */}
        {allItems.length > 0 && (
          <Box>
            {showSubSections ? (
              <>
                {renderSubsection('Prepared meal', preparedItems)}
                {renderSubsection('Meal kit', mealKitItems)}
              </>
            ) : (
              renderMealItems(allItems)
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export default MealSection;
